import { advanceFromKbdField } from '@/hooks/formKeyboardNav'
import { ChevronsUpDown } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

interface Option {
	value: string
	label: string
}

interface SearchableSelectProps {
	value: string
	options: Option[]
	placeholder?: string
	onChange: (val: string) => void
	disabled?: boolean
	'data-kbd-field'?: string
	'data-testid'?: string
}

export function SearchableSelect({
	value,
	options,
	placeholder = 'Select...',
	onChange,
	disabled = false,
	'data-kbd-field': dataKbdField,
	'data-testid': dataTestId,
}: SearchableSelectProps) {
	const [open, setOpen] = useState(false)
	const [search, setSearch] = useState('')
	const containerRef = useRef<HTMLDivElement>(null)
	const triggerRef = useRef<HTMLButtonElement>(null)

	const selectOption = (val: string) => {
		onChange(val)
		setOpen(false)
		setSearch('')
		if (dataKbdField !== undefined && triggerRef.current !== null) {
			requestAnimationFrame(() => {
				if (triggerRef.current !== null) {
					advanceFromKbdField(triggerRef.current)
				}
			})
		}
	}

	const filteredOptions = options.filter(opt => opt.label.toLowerCase().includes(search.toLowerCase()))

	// Close dropdown if clicked outside
	useEffect(() => {
		const handleClickOutside = (e: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
				setOpen(false)
				setSearch('')
			}
		}
		document.addEventListener('mousedown', handleClickOutside)
		return () => document.removeEventListener('mousedown', handleClickOutside)
	}, [])

	return (
		<div className='relative w-full' ref={containerRef}>
			{/* Trigger button */}
			<button
				ref={triggerRef}
				type='button'
				disabled={disabled}
				data-kbd-field={dataKbdField}
				data-testid={dataTestId}
				onClick={() => setOpen(prev => !prev)}
				className='w-full text-left px-3 py-2 border cursor-pointer rounded-md hover:border-border flex justify-between items-center'>
				{options.find(o => o.value === value)?.label ?? placeholder}
				<ChevronsUpDown className='ml-2 size-(--icon-standalone) opacity-50' />
			</button>

			{open && (
				<div
					data-testid={dataTestId ? `${dataTestId}-popover` : undefined}
					className='fixed mt-1 w-[calc(100%-3rem)] bg-background border rounded-md shadow-levitating max-h-60 overflow-y-auto z-[9999]'>
					<input
						type='text'
						placeholder='Search...'
						value={search}
						onChange={e => setSearch(e.target.value)}
						data-testid={dataTestId ? `${dataTestId}-search` : undefined}
						className='w-full px-3 py-2 border-b focus:outline-none'
					/>

					{/* Options list */}
					<div>
						{filteredOptions.length > 0 ? (
							filteredOptions.map(opt => (
								<div
									key={opt.value}
									onClick={() => selectOption(opt.value)}
									onKeyDown={e => {
										if (e.key === 'Enter' || e.key === ' ') {
											selectOption(opt.value)
										}
									}}
									className={`px-3 py-2 cursor-pointer hover:bg-muted flex items-center gap-2 ${
										value === opt.value && 'font-semibold bg-muted'
									}`}>
									{opt.label}
								</div>
							))
						) : (
							<div className='px-3 py-2 text-muted-foreground'>No results found</div>
						)}
					</div>
				</div>
			)}
		</div>
	)
}
